import { findIndex } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	AccessDeniedError,
	EmptyResponse,
	Guid,
	IMobizHttpService,
	IMobizStateService,
	IServerApiBuilderService,
	PagedResults,
	ParameterValueType,
	SerializableObjectList,
} from '../../common-react/core';
import { Inject, Injectable, InjectionToken } from '../../common-react/injection';

import { Country } from '../data/country.do';
import { CustomsDocumentInformation } from '../data/customs-document-information.do';
import { Party } from '../data/party.do';
import { PostalCodeVerification } from '../data/postal-code-verification.do';
import { PostalCode } from '../data/postal-code.do';
import { DEFAULT_SENDER_POSTAL_CODE, PostalDropOption } from '../data/postal-drop-options.do';
import { ShipmentDocument } from '../data/shipment-document.do';
import { ShipmentDetails } from '../data/shipment-details.do';
import { ShipmentFee } from '../data/shipment-fee.do';
import { ShipmentInformation } from '../data/shipment-information.do';
import { ShipmentRegistrationForm } from '../data/shipment-registration-form.do';
import { ShipmentTrackingEvent } from '../data/shipment-tracking-event.do';
import { ShipmentParcelUnlockCodes } from '../data/shipment-unlock-codes.do';
import { Shipment } from '../data/shipment.do';
import { Verification } from '../data/verification.do';
import { PosturinnApiEndpointConfiguration } from '../other/api-endpoint-configuration.interface';
import { PosturinnServerApi } from '../other/posturinn-server-api.enum';
import { ShipmentDirection } from '../other/shipment-direction.enum';
import { ShipmentStatus } from '../other/shipment-status.enum';
import { IReturn } from '../../../interfaces/returns/return';


export interface IShipmentsQueryParameters extends Record<string, ParameterValueType> {
  active?: boolean;
  direction?: ShipmentDirection;
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ShipmentStatus;
}

export interface IShipmentService {
  getShipment(shipmentId: Guid): Observable<Shipment>;

  deleteShipment(shipmentId: Guid): Observable<EmptyResponse>;
  
  setShipmentVisibility(shipmentId: Guid, hide: boolean): Observable<EmptyResponse>;

  /** @deprecated Please use generic search utils on the returned list of shipments. */
  getShipmentById(list: Shipment[], shipmentId: Guid): Shipment | undefined;

  verifyPostalCode(country: string, postalCode: string): Observable<PostalCodeVerification>;

  verifyRecipient(recipient: Party): Observable<Verification>;

  getShipments(queryParameters?: IShipmentsQueryParameters): Observable<PagedResults<Shipment>>;

  getShipmentDetails(shipmentId: Guid): Observable<ShipmentDetails>;

  registerShipment(shipment: ShipmentRegistrationForm): Observable<Shipment>;

  fetchPdf(shipmentId: string): string;

  registerShipmentForAnonymous(shipment: ShipmentRegistrationForm): Observable<Shipment>;

  fetchPdfForAnonymous(shipmentId: string, registrationKey: string, senderNiN: string): string;

  changeShipmentDestination(shipmentId: Guid, information: ShipmentInformation ): Observable<EmptyResponse>;

  getDropOptions(shipmentId: string, senderPostalCode?: string): Observable<PostalDropOption[]>;

  addCustomsInformation(shipmentId: Guid, information: CustomsDocumentInformation): Observable<ShipmentDocument>;

  getShipmentDocuments(shipmentId: Guid): Observable<ShipmentDocument[]>;

  createShipmentDocument(shipmentDocument: Omit<ShipmentDocument, 'id'>): Observable<EmptyResponse>;

  getShipmentDocument(shipmentId: Guid, documentId: Guid): Observable<ShipmentDocument>;

  getShipmentDocumentContentUrl(shipmentId: Guid, documentId: Guid): string;

  getShipmentFees(shipmentId: Guid, summarizeFees?: boolean): Observable<ShipmentFee[]>;

  getShipmentTrackingEvents(shipmentId: Guid): Observable<ShipmentTrackingEvent[]>;

  getShipmentParcelUnlockCodes(shipmentId: Guid): Observable<ShipmentParcelUnlockCodes>;

  getPublicShipment(shipmentId: Guid, language: string): Observable<Shipment>;

  getCountries(language: string): Observable<Country>;

  getPostalCodes(language: string): Observable<PostalCode>;
  
  sendReturnEmail(returnInfo: IReturn, shipmentId: string): Observable<EmptyResponse>;
}

export const IShipmentService
  = new InjectionToken<IShipmentService>('IShipmentService');

const acceptLanguageHeader = 'X-Preferred-Language';

@Injectable()
export class ShipmentService implements IShipmentService {
  private get accountId(): string {
    if (this.stateService.currentMembership.value === null) {
      throw new AccessDeniedError('Missing membership data.');
    }
    return this.stateService.currentMembership.value.organizationId;
  }

  constructor(
    @Inject(IMobizHttpService)
    private httpService: IMobizHttpService,
    @Inject(IServerApiBuilderService)
    private readonly serverApiBuilderService: IServerApiBuilderService,
    @Inject(IMobizStateService)
    private stateService: IMobizStateService,
  ) {
  }

  public getShipment(shipmentId: Guid): Observable<Shipment> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.Shipment,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
    };
    return this.httpService.get(new Shipment(), config);
  }

  public deleteShipment(shipmentId: Guid): Observable<EmptyResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.Shipment,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
    };
    return this.httpService.delete(new EmptyResponse(), config);
  }
  
  public setShipmentVisibility(shipmentId: Guid, hide: boolean): Observable<EmptyResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: hide ? PosturinnServerApi.ShipmentHide : PosturinnServerApi.ShipmentUnhide,
      urlParameters: {
        accountId: this.accountId,
      },
      queryParameters: {
        shipmentId: shipmentId
      }
    };
    return this.httpService.post(new EmptyResponse(), config);
  }

  /** @deprecated Please use generic search utils on the returned list of shipments. */
  public getShipmentById(list: Shipment[], shipmentId: Guid): Shipment | undefined {
    if (!list) {
      return undefined;
    }
    let foundIndex: number | undefined;
    foundIndex = findIndex(list, { id: shipmentId });
    if (foundIndex !== undefined && foundIndex !== -1) {
      return list[foundIndex];
    }
    return undefined;
  }

  public verifyPostalCode(countryCode: string, postalCode: string): Observable<PostalCodeVerification> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.VerifyPostalCode,
      queryParameters: {
        countryCode: countryCode,
        postalCode: postalCode
      },
    };
    return this.httpService.get(new PostalCodeVerification(), config);
  }

  public verifyRecipient(recipient: Party): Observable<Verification> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.VerifyRecipient,
      body: JSON.stringify(recipient),
    };
    return this.httpService.post(new Verification(), config);
  }

  public getShipments(queryParameters?: IShipmentsQueryParameters): Observable<PagedResults<Shipment>> {
    const updatedQueryParameters: Record<string, ParameterValueType> = {...queryParameters};
    if (typeof updatedQueryParameters.direction === 'string') {
      updatedQueryParameters.direction = updatedQueryParameters.direction.toLowerCase();
    }
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.Shipments,
      queryParameters: updatedQueryParameters,
      urlParameters: {
        accountId: this.accountId,
      },
    };
    const pagedResults: PagedResults<Shipment> = new PagedResults(Shipment);
    return this.httpService.get(pagedResults, config);
  }

  public getShipmentDetails(shipmentId: Guid): Observable<ShipmentDetails> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentDetails,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
    };
    return this.httpService.get(new ShipmentDetails(), config);
  }

  public registerShipment(shipment: ShipmentRegistrationForm): Observable<Shipment> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.Shipments,
      urlParameters: {
        accountId: this.accountId,
      },
      body: shipment.asRequestBody(),
    };
    return this.httpService.post(new Shipment(), config);
  }

  public fetchPdf(shipmentId: string): string {
    const urlParameters: Record<string, string> = {
      accountId: this.accountId,
      shipmentId: shipmentId,
    };
    return this.serverApiBuilderService.getUrl(PosturinnServerApi.FetchPdf, urlParameters);
  }

  public changeShipmentDestination(_shipmentId: Guid, information: ShipmentInformation): Observable<EmptyResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ChangeShipmentDestination,
      urlParameters: {
        accountId: this.accountId,
      },
      body: JSON.stringify(information),
    };
    return this.httpService.put(new EmptyResponse(), config);
  }

  public registerShipmentForAnonymous(shipment: ShipmentRegistrationForm): Observable<Shipment> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.RegisterShipmentForAnonymous,
      body: shipment.asRequestBody(),
    };
    return this.httpService.post(new Shipment(), config);
  }

  public fetchPdfForAnonymous(shipmentId: string, registrationKey: string, senderNiN: string): string {
    const urlParameters: Record<string, string> = {
      shipmentId: shipmentId,
    };
    const queryParameters: Record<string, string> = {
      senderNiN: senderNiN,
      registrationKey: registrationKey,
    };
    return this.serverApiBuilderService.getUrl(PosturinnServerApi.FetchPdfForAnonymous, urlParameters, queryParameters);
  }

  public getDropOptions(shipmentId: string, senderPostalCode?: string): Observable<PostalDropOption[]> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.PostalDropOption,
      urlParameters: {
        shipmentId: shipmentId,
      },
      queryParameters: {
        postalCode: senderPostalCode || DEFAULT_SENDER_POSTAL_CODE
      },
    };
    const list: SerializableObjectList<PostalDropOption> = new SerializableObjectList(PostalDropOption);
    return this.httpService.get(list, config).pipe(
      map((response: SerializableObjectList<PostalDropOption>): PostalDropOption[] => {
        return response.items ? response.items : [];
      }),
    );
  }

  public getShipmentDocuments(shipmentId: Guid): Observable<ShipmentDocument[]> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentDocuments,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
    };
    const list: SerializableObjectList<ShipmentDocument> = new SerializableObjectList(ShipmentDocument);
    return this.httpService.get(list, config).pipe(
      map((response: SerializableObjectList<ShipmentDocument>): ShipmentDocument[] => {
        return response.items ? response.items : [];
      }),
    );
  }

  public addCustomsInformation(shipmentId: Guid, information: CustomsDocumentInformation): Observable<ShipmentDocument> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentCustomsInfo,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
      body: information.toFormData(),
    };

    return this.httpService.post(new ShipmentDocument(), config);
  }

  public createShipmentDocument(shipmentDocument: Omit<ShipmentDocument, 'id'>): Observable<EmptyResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentDocuments,
      urlParameters: {
        accountId: this.accountId,
      },
      body: JSON.stringify(shipmentDocument),
    };
    return this.httpService.post(new EmptyResponse(), config);
  }

  public getShipmentDocument(shipmentId: Guid, documentId: Guid): Observable<ShipmentDocument> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentDocument,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
        documentId: documentId,
      },
    };
    return this.httpService.get(new ShipmentDocument(), config);
  }

  public getShipmentDocumentContentUrl(shipmentId: Guid, documentId: Guid): string {
    const urlParameters: Record<string, string> = {
      accountId: this.accountId,
      shipmentId: shipmentId,
      documentId: documentId,
    };
    return this.serverApiBuilderService.getUrl(PosturinnServerApi.ShipmentDocumentContent, urlParameters);
  }

  public getShipmentFees(shipmentId: Guid, summarizeFees: boolean = false): Observable<ShipmentFee[]> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentFees,
      queryParameters: {
        summarize: summarizeFees
      },
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
    };
    const list: SerializableObjectList<ShipmentFee> = new SerializableObjectList(ShipmentFee);
    return this.httpService.get(list, config).pipe(
      map((response: SerializableObjectList<ShipmentFee>): ShipmentFee[] => {
        return response.items ? response.items : [];
      }),
    );
  }

  public getShipmentTrackingEvents(shipmentId: Guid): Observable<ShipmentTrackingEvent[]> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentTrackingEvents,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
    };
    const list: SerializableObjectList<ShipmentTrackingEvent> = new SerializableObjectList(ShipmentTrackingEvent);
    return this.httpService.get(list, config).pipe(
      map((response: SerializableObjectList<ShipmentTrackingEvent>): ShipmentTrackingEvent[] => {
        return response.items ? response.items : [];
      }),
    );
  }

  public getShipmentParcelUnlockCodes(shipmentId: Guid): Observable<ShipmentParcelUnlockCodes> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ShipmentUnlockCodes,
      urlParameters: {
        accountId: this.accountId,
        shipmentId: shipmentId,
      },
    };
    return this.httpService.get(new ShipmentParcelUnlockCodes(), config);
  }

  public getPublicShipment(shipmentId: Guid, language: string): Observable<Shipment> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.PublicShipments,
      headers: {
        [acceptLanguageHeader]: language
      },
      urlParameters: {
        shipmentId: shipmentId
      },
    };
    return this.httpService.get(new Shipment(), config);
  }

  public getCountries(language: string): Observable<Country> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.Countries,
      headers: {
        [acceptLanguageHeader]: language
      }
    };
    return this.httpService.get(new Country(), config);
  }

  public getPostalCodes(language: string): Observable<PostalCode> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.PostalCodes,
      headers: {
        [acceptLanguageHeader]: language
      }
    };
    return this.httpService.get(new PostalCode(), config);
  }
  
  public sendReturnEmail(returnInfo: IReturn, shipmentId: string): Observable<EmptyResponse> {
    // Used for Metta sport returns
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.CompanyReturnEmail,
       urlParameters: {
         accountId: this.accountId,
       },
      body: JSON.stringify({
        ...returnInfo,
        shipmentId: shipmentId
      }),
    };
    return this.httpService.post(new EmptyResponse(), config);
  }
}
